<template>
  <div v-if="infoSaldoVale != null && $localStorageService.can('BT_restitucion_saldo_vale_paciente')">
  <div class="vale-container">

    <a class="agregar-vale mr-5" @click="toggleVale">
      <em class="blue_plus"></em> AGREGAR MÁS SALDO VALE
    </a>

    <transition name="vale">
      <div v-if="mostrarPanelVale" class="vale-dropdown">
        <b-alert v-if="saldoCompleto" variant="danger" show class="py-1 mb-2">
          <small>El saldo del vale ya está completo. No puede agregar más saldo.</small>
        </b-alert>
        <b-alert v-if="!saldoCompleto" variant="warning" size="sm" show>
          <small>Solo puede agregar {{ getSaldoPermitido | moneda }} al saldo vale</small>
        </b-alert>
        <b-form ref="formSaldoVale" @submit.prevent="guardar" :disabled="saldoCompleto">
          <div class="d-flex align-items-center mb-2">
            <label class="text-left pr-2" style="flex: 1" for="monto">
              Monto a agregar
            </label>

            <div style="flex: 1">
              <b-form-input
                  size="sm"
                  v-model="monto"
                  type="number"
                  :state="montoState"
                  @blur="touchedMonto = true"
                  min="1"
                  step="1"
                  @keydown="bloquearDecimal"
                  @input="forzarEntero"
                  :disabled="saldoCompleto">
              </b-form-input>

              <b-form-invalid-feedback v-if="montoError">
                {{ montoError }}
              </b-form-invalid-feedback>
            </div>
          </div>

          <div class="d-flex align-items-center mb-2">
            <label class="text-left pr-2" style="flex: 1" for="fecha">
              Fecha
            </label>
            <label class="mt-2 text-left" style="flex: 1" for="fecha">
              {{ fecha | formatoFecha }}
            </label>
          </div>

          <div class="d-flex mb-2">
            <label class="text-left pr-2 mt-1" style="flex: 1" for="motivo">
              Motivo
            </label>

            <div style="flex: 1">
              <b-form-textarea
                  size="sm"
                  v-model="motivo"
                  :state="motivoState"
                  style="flex: 1"
                  @blur="touchedMotivo = true"
                  :disabled="saldoCompleto">
              </b-form-textarea>

              <b-form-invalid-feedback v-if="!motivoState">
                {{ motivoError }}
              </b-form-invalid-feedback>
            </div>
          </div>

          <div class="d-flex justify-content-center mb-2">
            <b-button
                block
                class="mt-3 px-5 w-auto"
                type="submit"
                size="sm"
                :disabled="!formValido || loadingGuardar">
              <b-spinner small v-if="loadingGuardar"></b-spinner>
              AGREGAR
            </b-button>
          </div>


        </b-form>

        <!-- TABLA -->
        <div v-if="obtenerMovimientos.length" class="pt-2 tabla-scroll">
          <table class="tabla-movimientos">
            <thead>
            <tr>
              <th class="text-left">Fecha</th>
              <th class="text-center">Monto</th>
              <th class="text-center">Motivo</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="mov in obtenerMovimientos" :key="mov.idMovimiento">
              <td>{{ mov.fechaUltimaActualizacion | formatoFecha }}</td>
              <td>{{ mov.monto | moneda }}</td>
              <td>{{ mov.motivo }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </transition>

  </div>
  </div>
</template>

<script>
import moment from "moment/moment";

/**
 * @component restituirSaldoValePaciente
 * @description Componente que permite agregar saldo adicional (restitución) al vale de un paciente.
 * Muestra un panel desplegable con un formulario para ingresar monto y motivo,
 * y una tabla con el historial de movimientos de restitución.
 *
 * @emits {void} refreshSaldo - Se emite al padre cuando se registra un movimiento exitoso,
 *   para que actualice la información del saldo vale.
 */
export default {
  name: "restituirSaldoValePaciente",
  /**
   * Información del saldo vale del paciente.
   * @type {Object}
   * @property {number} saldoActual    - Saldo disponible actualmente en el vale.
   * @property {number} saldoOriginal  - Saldo original/máximo permitido del vale.
   * @property {number} idPacienteVale - Identificador del vale del paciente.
   * @property {Array}  movimientos    - Historial de movimientos del vale.
   */
  props: {
    infoSaldoVale: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      mostrarPanelVale: false,
      monto: null,
      fecha: moment(new Date()).format("YYYY-MM-DD"),
      motivo: null,
      touchedMonto: false,
      touchedMotivo: false,
      loadingGuardar: false,
    }
  },
  computed: {
    /**
     * Filtra y retorna únicamente los movimientos de restitución con motivo registrado.
     * @returns {Array} Lista de movimientos de tipo restitución.
     */
    obtenerMovimientos() {
      return this.infoSaldoVale?.movimientos?.filter(movimiento =>
          movimiento.esRestitucion && movimiento.motivo
      ) || [];
    },

    /**
     * Estado de validación del campo monto para Bootstrap Vue.
     * Retorna null si no fue tocado, true si es válido, false si hay error.
     * @returns {boolean|null}
     */
    montoState() {
      if (!this.touchedMonto) return null
      if (this.monto === null || this.monto === '') return false
      if (this.monto <= 0) return false
      if (this.excedeSaldo) return false
      if (!Number.isInteger(Number(this.monto))) return false
      return true
    },

    /**
     * Mensaje de error para el campo monto según la validación fallida.
     * @returns {string}
     */
    montoError() {
      if (!this.touchedMonto) return ''
      if (this.monto === null || this.monto === '') return 'El monto es requerido'
      if (this.monto <= 0) return 'Ingresa un monto mayor a 0'
      if (this.excedeSaldo) return 'El monto excede el saldo permitido'
      if (!Number.isInteger(Number(this.monto))) return 'El monto debe ser un número entero'
      return ''
    },

    /**
     * Verifica si el monto ingresado haría que el saldo actual supere el saldo original.
     * @returns {boolean} true si el monto excede el permitido.
     */
    excedeSaldo() {
      if (!this.monto) return false
      let saldoFinal = Number(this.infoSaldoVale.saldoActual) + Number(this.monto);
      let saldoOriginal = Number(this.infoSaldoVale.saldoOriginal);
      return saldoFinal > saldoOriginal;
    },

    /**
     * Estado de validación del campo motivo para Bootstrap Vue.
     * Requiere al menos entre 3 y 150 caracteres.
     * @returns {boolean|null}
     */
    motivoState() {
      if (!this.touchedMotivo) return null
      if (!this.motivo) return false
      return this.motivo.length >= 3 && this.motivo.length <= 150
    },

    /**
     * Mensaje de error para el campo motivo según la validación fallida.
     * @returns {string}
     */
    motivoError() {
      if (!this.touchedMotivo) return ''
      if (!this.motivo) return 'El motivo es requerido'
      if (this.motivo.length < 3) return 'Debe tener al menos 3 caracteres'
      if (this.motivo.length > 150) return 'El motivo no puede exceder los 150 caracteres'
      return ''
    },

    /**
     * Indica si el formulario completo es válido (monto y motivo correctos).
     * @returns {boolean}
     */
    formValido() {
      return this.montoState === true && this.motivoState === true
    },

    /**
     * Calcula cuánto saldo se puede agregar (diferencia entre saldo original y actual).
     * @returns {number} Cantidad permitida a agregar.
     */
    getSaldoPermitido() {
      if(!this.infoSaldoVale) return 0;
      return Number(this.infoSaldoVale.saldoOriginal) - Number(this.infoSaldoVale.saldoActual);
    },

    /**
     * Determina si el saldo vale ya está al máximo (saldoActual >= saldoOriginal).
     * @returns {boolean}
     */
    saldoCompleto() {
      if (!this.infoSaldoVale) return false
      return Number(this.infoSaldoVale.saldoActual) >= Number(this.infoSaldoVale.saldoOriginal)
    }

  },
  methods: {
    /**
     * Alterna la visibilidad del panel desplegable.
     * Si se abre, reinicia el formulario para limpiar valores previos.
     */
    toggleVale() {
      this.mostrarPanelVale = !this.mostrarPanelVale;

      if(this.mostrarPanelVale){
        this.resetForm();
      }
    },

    /**
     * Valida y envía el formulario para registrar un movimiento de restitución.
     * Llama al endpoint POST /vale/pacientes/movimientos con monto, motivo e idPacienteVale.
     * Al terminar (éxito o error): resetea el formulario, cierra el panel y emite `refreshSaldo`.
     */
    guardar(){
      if(this.saldoCompleto){
        this.$swal.fire({
          icon: "warning",
          title: "Saldo completo",
          text: "El saldo del vale ya no permite agregar más saldo",
        });
        return;
      }

      if(!this.formValido) return;

      this.loadingGuardar = true;

      const body = {
        monto: this.monto,
        motivo: this.motivo,
        idPacienteVale: this.infoSaldoVale.idPacienteVale,
        esRestitucion: true,
        esAbono: true
      };

      this.$http.post(this.$api+"vale/pacientes/movimientos", body)
          .then(res => {
            console.log(res);
            this.$swal.fire({
              icon: "success",
              title: "Éxito",
              text: "Se ha realizado correctamente el movimiento en el saldo vale del paciente",
            });

          })
          .catch(err => {
            console.log(err);
            this.$swal.fire({
              icon: "error",
              title: "Error",
              text: err.response.data,
            });
          })
          .finally(() => {
            this.loadingGuardar = false;
            this.resetForm();
            this.mostrarPanelVale = false;
            this.$emit("refreshSaldo");
          })
    },

    /**
     * Limpia todos los campos del formulario y restablece los estados de validación.
     * Usa $nextTick para hacer el reset del componente b-form de Bootstrap Vue.
     */
    resetForm(){
      this.monto = null;
      this.motivo = null;
      this.touchedMonto = false;
      this.touchedMotivo = false;

      this.$nextTick(() => {
        this.$refs.formSaldoVale?.reset();
      });
    },

    /**
     * Manejador del evento click global.
     * Cierra el panel desplegable si el clic ocurre fuera del componente.
     * @param {MouseEvent} e - Evento del clic del documento.
     */
    clickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.mostrarPanelVale = false
      }
    },

    /**
     * Bloquea la tecla punto, coma y notación científica (e/E)
     * en el campo de monto para impedir que el usuario escriba decimales.
     * @param {KeyboardEvent} e
     */
    bloquearDecimal(e) {
      if (['.', ',', 'e', 'E', '+'].includes(e.key)) {
        e.preventDefault()
      }
    },

    /**
     * Si el valor contiene decimales (ej. por Ctrl+V), lo trunca a entero.
     * @param {string} valor
     */
    forzarEntero(valor) {
      if (valor && valor.includes('.')) {
        this.monto = String(Math.trunc(Number(valor)))
      }
    },
  },
  mounted() {
    // Registra el listener global para cerrar el panel al hacer clic fuera del componente
    document.addEventListener("click", this.clickOutside);
  },
  beforeDestroy() {
    // Limpia el listener global para evitar memory leaks al destruir el componente
    document.removeEventListener("click", this.clickOutside);
  }
}
</script>

<style scoped>
.vale-container {
  position: relative;
  display: inline-block;
}

.agregar-vale {
  cursor: pointer;
  color: var(--color-dentalia) !important;
  font-weight: 600;
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
  z-index: 11;
  background: white;
  padding: 0 8px;
}

.vale-dropdown {
  position: absolute;
  right: 10px;
  top: 10px;
  width: 330px;
  background: white;
  border: 2px solid rgb(236, 236, 236);
  border-radius: 6px;
  padding: 16px;
  z-index: 10;
}

.tabla-movimientos {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.tabla-movimientos thead {
  border-bottom: 2px solid #2bb3e6;
  border-top: 2px solid #2bb3e6;
  position: sticky;
  top: -10px;
  background: #fff;
}

.tabla-movimientos th {
  text-align: left;
  padding: 5px;
}

.tabla-movimientos td {
  padding: 6px;
  background: #cfe8f3;
}

.tabla-movimientos tbody tr {
  border-bottom: 2px solid #2bb3e6;
}

.vale-enter-active,
.vale-leave-active {
  transition: all 0.25s ease;
}

.vale-enter {
  opacity: 0;
  transform: translateY(-10px);
}

.vale-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.tabla-scroll {
  max-height: 185px;
  overflow-y: auto;
}

.tabla-movimientos tr td:nth-child(3) {
  overflow-wrap: anywhere;
}
</style>
